import Feature from 'ol/Feature';
import GeoJSONFormat from 'ol/format/GeoJSON';
import { LineString } from 'ol/geom';
import { bbox as bboxLoadingStrategy } from 'ol/loadingstrategy';
import VectorSource from 'ol/source/Vector';

import type { Map as OlMap } from 'cadenza/map/map';
import { getLayerFeatures } from 'cadenza/api-client/vector-layer-api';
import { context } from 'cadenza/map/context/context';
import { FeatureProperty } from 'cadenza/map/feature-property';
import { LayerProperty } from 'cadenza/map/layer-property';
import { SourceProperty } from 'cadenza/map/source-property';
import type { Attributes } from 'cadenza/map/style/feature-label-style-resolver';
import { FeatureLabelStyleResolver } from 'cadenza/map/style/feature-label-style-resolver';
import { FeatureStyleResolver } from 'cadenza/map/style/feature-style-resolver';
import { LINE_TO_PREDECESSOR_STYLE } from 'cadenza/map/timeline-visualization/timeline-constants';
import { createArrowToPreviousCoordinate } from 'cadenza/map/timeline-visualization/arrow-to-previous-coordinate';
import { ViewProperty } from 'cadenza/map/view-property';
import { assert } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import { isProblem } from 'cadenza/utils/problem-util';
import { mergeStyles } from 'cadenza/map/style/style-util';
import { getMapPixelRatio } from 'cadenza/map/map-utils';
import {
  RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT,
  RESULT_TRUNCATED_BY_ROW_COUNT
} from 'cadenza/map/custom/workbook/source-message-codes';
import type { FeatureDto } from 'cadenza/map/dto/feature-dto';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { TimelinePoint } from 'cadenza/api-client/timeline-api';
import type { ReferenceDto } from 'cadenza/api-client/dto/reference-dto';
import type { LoadingStrategyName } from 'cadenza/api-client/dto/layer-dto';

import type { LoadingStrategy } from 'ol/source/Vector';
import type { Geometry } from 'ol/geom';
import type { StyleFunction, StyleLike } from 'ol/style/Style';
import type FeatureFormat from 'ol/format/Feature';
import type { Extent } from 'ol/extent';
import type JSONFeature from 'ol/format/JSONFeature';

const LABELS_COUNT_LIMIT_CROSSED_MESSAGE = 'layer.warning.labelsCountLimitCrossed';

export const VECTOR_LAYER_SOURCE_FEATURES_LOAD_END = 'featuresloadend';
export const VECTOR_LAYER_SOURCE_FEATURES_LOAD_START = 'featuresloadstart';

const LOADING_STRATEGIES_FACTORY: Record<LoadingStrategyName, (map?: OlMap, layerId?: string) => LoadingStrategy> = {
  BOUNDING_BOX: () => bboxLoadingStrategy,
  MAX_LAYER_EXTENT: (map?: OlMap, layerId?: string) => maxLayerExtentStrategy(map!, layerId!)
};
const DEFAULT_LOADING_STRATEGY: LoadingStrategyName = 'BOUNDING_BOX';

function maxLayerExtentStrategy (map: OlMap, layerId: string) {
  return () => {
    const layer = context(map).getLayer(layerId);
    const layerSuggestedExtent = layer?.get(LayerProperty.SUGGESTED_EXTENT);
    const mapMaxExtent = map.getView().get(ViewProperty.MAX_EXTENT);

    return [ layerSuggestedExtent || mapMaxExtent ];
  };
}

/**
 * Vector layer source loads the styled features from the server to render them in the browser.
 */
export class VectorLayerSource extends VectorSource {

  readonly #map;
  readonly #layerRenderBuffer;
  _url;
  _areTimelinePointsConnectedWithLines;
  _featureAttributes;
  _labelAttributeNames;
  #messages?: { type: string; code: string }[];
  #lastExtent?: Extent;
  _cachedZoom = { zoom: -1 };
  readonly #labelCountLimit;

  constructor (map: OlMap, layerId: string, {
    url,
    loadingStrategy,
    layerRenderBuffer,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames = [],
    labelCountLimit
  }: { url?: string;
    loadingStrategy?: LoadingStrategyName;
    layerRenderBuffer: number;
    areTimelinePointsConnectedWithLines?: boolean;
    timelineTimestampAttributeName?: string;
    labelAttributeNames: string[];
    labelCountLimit: number;
  } = {
    loadingStrategy: DEFAULT_LOADING_STRATEGY,
    layerRenderBuffer: 0,
    labelAttributeNames: [],
    labelCountLimit: 500 }) {
    const strategy = loadingStrategy ? LOADING_STRATEGIES_FACTORY[loadingStrategy](map, layerId) as LoadingStrategy : undefined;
    assert(strategy, `Unknown loading strategy ${loadingStrategy}`);
    assert(url, 'Missing vector layer source url');
    super({
      format: new GeoJSONFormat(),
      // this logic is to make our custom loader work in with ol/source/Vector.js. Loading property in there depends on
      // provided successCallback, failureCallback callbacks. More info about this logic here:
      // https://github.com/openlayers/openlayers/commit/431beed45fb53e80f92875d34b0d5e54b7a40aa9
      loader:
        (extent: Extent,
          _resolution: number,
          _projection,
          successCallback: ((features: Feature<Geometry>[]) => void) | undefined,
          failureCallback: (() => void) | undefined) =>
          this._loadFeatures(extent, successCallback, failureCallback),
      strategy
    });
    this.#map = map;
    this.#layerRenderBuffer = layerRenderBuffer;
    this._url = url;
    this._areTimelinePointsConnectedWithLines = areTimelinePointsConnectedWithLines;
    this._labelAttributeNames = labelAttributeNames;
    this.#labelCountLimit = labelCountLimit;
    this._featureAttributes = [ timelineTimestampAttributeName, ...labelAttributeNames ].filter(Boolean);

    map.getView().on('change', () => this._removeCurrentExtentFeaturesIfFeaturesAreTruncated());

    if (this._areTimelinePointsConnectedWithLines) {
      const view = map.getView();
      view.on('change', () => {
        const zoom = view.getZoom();
        if (zoom) {
          this._cachedZoom.zoom = Math.round(zoom!);
        }
      });
    }
  }

  _removeCurrentExtentFeaturesIfFeaturesAreTruncated () {
    const featuresTruncated = this.#messages?.some(m => {
      return m.code === RESULT_TRUNCATED_BY_ROW_COUNT || m.code === RESULT_TRUNCATED_BY_GEOMETRY_VERTEX_COUNT;
    });
    if (featuresTruncated && this.#lastExtent) {
      this.removeLoadedExtent(this.#lastExtent);
    }
  }

  override getUrl () {
    return this._url;
  }

  override setUrl (url: string, silent = false) {
    if (url === this._url) {
      return;
    }
    this._url = url;
    if (!silent) {
      this.refresh();
    }
  }

  async _loadFeatures (extent: Extent,
    successCallback: ((features: Feature<Geometry>[]) => void) | undefined,
    failureCallback: (() => void) | undefined) {
    const srsCode = this.#map.getView().getProjection().getCode();
    const mapSize: DimensionDto = [ ...this.#map.getSize()! ] as DimensionDto;
    mapSize[0] += this.#layerRenderBuffer * 2;
    mapSize[1] += this.#layerRenderBuffer * 2;

    try {
      let { result: res, messages } = await getLayerFeatures({
        url: this._url,
        srsCode,
        mapSize,
        extent: extent as WorldBoxDto,
        featureAttributes: this._featureAttributes
      });
      const { features, message } = await createOlFeatures(
        res,
        this.getFormat(),
        this._labelAttributeNames,
        this.#labelCountLimit,
        getMapPixelRatio(this.#map)
      );
      if (this._areTimelinePointsConnectedWithLines) {
        const lineWithArrowsFeatures = getLineWithArrowsFeatures(res.features, this.getFormat(), this.#map, this._cachedZoom);
        features.push(...lineWithArrowsFeatures);
      }
      // Merge messages
      if (messages && message) {
        messages = [ ...messages, message ];
      } else if (message) {
        messages = [ message ];
      }
      this.#messages = messages;
      this.#lastExtent = extent;

      this.clear(true);
      this.addFeatures(features);
      this.set(SourceProperty.MESSAGES, messages);
      if (successCallback) {
        successCallback(features);
      }
    } catch (error) {
      if (isProblem(error) && error.key === 'timeout') {
        logger.info('Failed to load vector layer features due to timeout.', error);
        this.set(SourceProperty.MESSAGES, [
          { type: 'ERROR', code: 'layer.errors.data.timeout' }
        ]);
      } else {
        logger.error('Failed to get vector layer features', error);
        this.set(SourceProperty.MESSAGES, [
          { type: 'ERROR', code: 'layer.errors.data.loadingFailed', args: { message: '' } }
        ]);
      }
      this.removeLoadedExtent(extent);
      if (failureCallback) {
        failureCallback();
      }
    }
  }

}

async function createOlFeatures (
  {
    styles = [],
    features = [],
    labelStyles = []
  },
  format: FeatureFormat | undefined,
  labelAttributeNames: string[],
  labelCountLimit: number,
  mapPixelRatio: number
) {
  const styleResolver = new FeatureStyleResolver(styles, mapPixelRatio);
  const labelStyleResolver = new FeatureLabelStyleResolver(labelStyles);
  const maxLabelCountChecker = new MaxLabelCountChecker(labelCountLimit);

  const olFeatures = await Promise.all(features.map(async ({
    featureId,
    geometry,
    attributes,
    style,
    labelStyle,
    labelPositions,
    labelRotations
  }: {
    featureId: number | string | undefined;
    geometry: Geometry;
    attributes: Attributes;
    style: string;
    labelStyle: ReferenceDto;
    labelPositions: number[][];
    labelRotations: number[];
  }) => {
    // OL Feature
    const feature = new Feature({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore // TODO (palys, CADENZA-33475) ts migration - fix ts ignore
      geometry: (format as JSONFeature)?.readGeometryFromObject(geometry)
    });
    feature.setId(featureId);

    const styleLike = styleResolver.resolve(style, feature.getGeometry()?.getType());

    // Attributes
    if (attributes) {
      feature.setProperties(attributes, true);
    }

    // Set main style if label style cannot or must not be rendered
    if (!isLabelRenderable(labelStyle, attributes)) {
      feature.setStyle(styleLike);
      return feature;
    }
    // Increase label count
    // Each position results in a label, thus we use labelPositions.length for the counter.
    // We cannot use olFeatureLabelStyles.length since it is resolved asynchronously,
    // but we require it earlier.
    maxLabelCountChecker.add(labelPositions.length);
    if (maxLabelCountChecker.isLimitReached()) {
      feature.setStyle(styleLike);
      return feature;
    }

    // Label Style
    const mainStyle = getMainStyle(feature, styleLike);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore // TODO (palys, CADENZA-33475) ts migration - fix ts ignore
    const zIndex = mainStyle?.getZIndex() ? (mainStyle.getZIndex()!  + 1) : 0;
    const createdLabelStyles = createLabelStyles(labelStyleResolver, {
      attributes,
      labelStyle,
      labelAttributeNames,
      labelPositions,
      labelRotations,
      zIndex
    });

    feature.setStyle(mergeStyles(styleLike, createdLabelStyles));
    feature.set(FeatureProperty.IS_LABELED, true);
    return feature;
  }));

  let message;
  if (maxLabelCountChecker.isLimitReached()) {
    message = { type: 'WARNING', code: LABELS_COUNT_LIMIT_CROSSED_MESSAGE };
  }

  return {
    features: olFeatures,
    message
  };
}

function createLabelStyles (labelStyleResolver: FeatureLabelStyleResolver, {
  attributes,
  labelStyle,
  labelAttributeNames,
  labelPositions,
  labelRotations,
  zIndex
}: { attributes: Attributes;
  labelStyle: ReferenceDto;
  labelAttributeNames: string[];
  labelPositions: number[][];
  labelRotations: number[];
  zIndex: number;
}) {
  // Feature has attributes, we must generate labels
  if (labelPositions && labelRotations) {
    assert(labelPositions.length === labelRotations.length, 'Label position count should match label rotations count');
  }
  assert(areAllAttributesPresent(labelAttributeNames, attributes), 'Inconsistent attributes');
  return labelPositions.map((position, index: number) => {
    return labelStyleResolver.resolve({
      labelStyleReference: labelStyle.$ref,
      attributes,
      position,
      rotation: labelRotations[index],
      zIndex
    });
  });
}

function isLabelRenderable (labelStyle: ReferenceDto, attributes: Attributes) {
  return labelStyle && attributes && Object.keys(attributes).length;
}

function hasProperty (object: object, property: PropertyKey) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

function areAllAttributesPresent (labelAttributeNames: string[], attributes: Attributes) {
  return labelAttributeNames.every(name => hasProperty(attributes, name));
}

function getLineWithArrowsFeatures (featuresDto: FeatureDto[], format: FeatureFormat | undefined, map: OlMap, cachedZoom: object) {
  const imageCache: Map<string, object> = new Map();
  const lineStringCoords = [];
  const features = [];
  for (let index = 0; index < featuresDto.length; index++) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore // TODO (palys, CADENZA-33475) ts migration - fix ts ignore
    const pointCoordinates = (format as JSONFeature)?.readGeometryFromObject(featuresDto[index].geometry).getCoordinates();
    lineStringCoords.push(pointCoordinates);
    if (index > 0) {
      // TODO (palys, CADENZA-33475) ts migration - here probably different method should be used, only coordinate is provided
      const previousPoint: TimelinePoint  = {
        calculatedSpeed: 0,
        speed: 0,
        style: [],
        time: 0,
        coordinate: lineStringCoords[index - 1]
      };
      const endPoint: TimelinePoint = {
        calculatedSpeed: 0,
        speed: 0,
        style: [],
        time: 0,
        coordinate: pointCoordinates
      };
      features.push(createArrowToPreviousCoordinate(previousPoint, endPoint, imageCache, map, cachedZoom));
    }
  }
  if (lineStringCoords.length) {
    const lineFeature = new Feature({
      geometry: new LineString(lineStringCoords)
    });
    lineFeature.setStyle(LINE_TO_PREDECESSOR_STYLE);
    features.push(lineFeature);
  }
  return features;
}

function getMainStyle (feature: Feature<Geometry>, olStyle: StyleLike | StyleLike[] | StyleFunction): StyleLike {
  let style = olStyle;
  if (typeof style === 'function') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore // TODO (palys, CADENZA-33475) ts migration - fix ts ignore
    style = style(feature);
  }
  if (Array.isArray(style)) {
    style = style[0];
  }
  return style as StyleLike;
}

class MaxLabelCountChecker {

  _maxLabelCount;
  _currentLabelCount = 0;

  constructor (maxLabelCount: number) {
    this._maxLabelCount = maxLabelCount;
  }

  add (amount: number) {
    this._currentLabelCount += amount;
  }

  isLimitReached () {
    return this._currentLabelCount >= this._maxLabelCount;
  }

}
